import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Tabs,
  Tab,
  LinearProgress,
  Rating,
  TextField
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  CloudUpload as UploadIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Schedule as ScheduleIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Comment as CommentIcon
} from '@mui/icons-material';

export default function Submissions({ user }) {
  const [submissions, setSubmissions] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    // Mock submissions data - replace with Firebase queries
    setSubmissions([
      {
        id: 1,
        title: 'Epic Gaming Moment #1',
        campaignTitle: 'Gaming Highlights Compilation',
        campaignId: 1,
        videoUrl: 'https://example.com/video1.mp4',
        thumbnailUrl: 'https://example.com/thumb1.jpg',
        duration: '0:45',
        uploadDate: new Date(Date.now() - 2 * 60 * 60 * 1000),
        status: 'approved',
        rating: 4.5,
        earnings: 25.00,
        views: 1240,
        feedback: 'Great clip! Perfect timing and quality.',
        campaignCreator: 'GameMaster Pro'
      },
      {
        id: 2,
        title: 'Workout Motivation Clip',
        campaignTitle: 'Workout Motivation Videos',
        campaignId: 2,
        videoUrl: 'https://example.com/video2.mp4',
        thumbnailUrl: 'https://example.com/thumb2.jpg',
        duration: '1:20',
        uploadDate: new Date(Date.now() - 5 * 60 * 60 * 1000),
        status: 'pending',
        rating: null,
        earnings: 0,
        views: 0,
        feedback: null,
        campaignCreator: 'FitLife Channel'
      },
      {
        id: 3,
        title: 'Beach Sunset Timelapse',
        campaignTitle: 'Travel Adventure Clips',
        campaignId: 3,
        videoUrl: 'https://example.com/video3.mp4',
        thumbnailUrl: 'https://example.com/thumb3.jpg',
        duration: '0:30',
        uploadDate: new Date(Date.now() - 24 * 60 * 60 * 1000),
        status: 'rejected',
        rating: 2,
        earnings: 0,
        views: 0,
        feedback: 'Quality is good but doesn\'t match campaign requirements. Please ensure content aligns with adventure theme.',
        campaignCreator: 'Wanderlust Media'
      },
      {
        id: 4,
        title: 'Cooking Quick Tip',
        campaignTitle: 'Food & Recipe Shorts',
        campaignId: 4,
        videoUrl: 'https://example.com/video4.mp4',
        thumbnailUrl: 'https://example.com/thumb4.jpg',
        duration: '0:55',
        uploadDate: new Date(Date.now() - 48 * 60 * 60 * 1000),
        status: 'approved',
        rating: 4.8,
        earnings: 35.00,
        views: 2100,
        feedback: 'Excellent content! Clear instructions and great video quality.',
        campaignCreator: 'Chef\'s Corner'
      }
    ]);
  }, [user]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleViewSubmission = (submission) => {
    setSelectedSubmission(submission);
    setDialogOpen(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      case 'draft':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckIcon />;
      case 'pending':
        return <ScheduleIcon />;
      case 'rejected':
        return <CloseIcon />;
      default:
        return <ScheduleIcon />;
    }
  };

  const filteredSubmissions = submissions.filter(submission => {
    switch (tabValue) {
      case 0: // All
        return true;
      case 1: // Approved
        return submission.status === 'approved';
      case 2: // Pending
        return submission.status === 'pending';
      case 3: // Rejected
        return submission.status === 'rejected';
      default:
        return true;
    }
  });

  const SubmissionCard = ({ submission }) => (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ position: 'relative' }}>
        <Box
          sx={{
            height: 200,
            bgcolor: 'grey.200',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundImage: submission.thumbnailUrl ? `url(${submission.thumbnailUrl})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          {!submission.thumbnailUrl && (
            <PlayIcon sx={{ fontSize: 48, color: 'grey.400' }} />
          )}
        </Box>
        <Chip
          label={submission.status}
          color={getStatusColor(submission.status)}
          size="small"
          icon={getStatusIcon(submission.status)}
          sx={{ position: 'absolute', top: 8, right: 8 }}
        />
        <Chip
          label={submission.duration}
          size="small"
          sx={{ position: 'absolute', bottom: 8, right: 8, bgcolor: 'rgba(0,0,0,0.7)', color: 'white' }}
        />
      </Box>

      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold', mb: 1 }}>
          {submission.title}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Campaign: {submission.campaignTitle}
        </Typography>

        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
          Uploaded: {submission.uploadDate.toLocaleDateString()}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          {submission.status === 'approved' && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" color="success.main" sx={{ fontWeight: 'bold' }}>
                  +${submission.earnings}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ViewIcon sx={{ fontSize: 16 }} />
                <Typography variant="body2">{submission.views}</Typography>
              </Box>
            </>
          )}
        </Box>

        {submission.rating && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Rating value={submission.rating} precision={0.1} size="small" readOnly />
            <Typography variant="body2">({submission.rating})</Typography>
          </Box>
        )}
      </CardContent>

      <CardActions sx={{ justifyContent: 'space-between', p: 2 }}>
        <Button 
          size="small" 
          startIcon={<PlayIcon />}
          onClick={() => handleViewSubmission(submission)}
        >
          View
        </Button>
        
        {submission.status === 'rejected' && (
          <Button 
            size="small" 
            startIcon={<EditIcon />}
            color="primary"
          >
            Resubmit
          </Button>
        )}
        
        {submission.status === 'approved' && (
          <Button 
            size="small" 
            startIcon={<DownloadIcon />}
            color="success"
          >
            Download
          </Button>
        )}

        <IconButton size="small">
          <DeleteIcon />
        </IconButton>
      </CardActions>
    </Card>
  );

  const totalEarnings = submissions
    .filter(s => s.status === 'approved')
    .reduce((sum, s) => sum + s.earnings, 0);

  const totalViews = submissions
    .filter(s => s.status === 'approved')
    .reduce((sum, s) => sum + s.views, 0);

  const approvalRate = submissions.length > 0 
    ? (submissions.filter(s => s.status === 'approved').length / submissions.length * 100).toFixed(1)
    : 0;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          My Submissions
        </Typography>
        <Button
          variant="contained"
          startIcon={<UploadIcon />}
          sx={{ bgcolor: '#ff5252', '&:hover': { bgcolor: '#ff3939' } }}
        >
          New Submission
        </Button>
      </Box>

      {/* Stats Overview */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="success.main" sx={{ fontWeight: 'bold' }}>
              ${totalEarnings}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Earnings
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="primary.main" sx={{ fontWeight: 'bold' }}>
              {totalViews.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Views
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="info.main" sx={{ fontWeight: 'bold' }}>
              {approvalRate}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Approval Rate
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={`All (${submissions.length})`} />
          <Tab label={`Approved (${submissions.filter(s => s.status === 'approved').length})`} />
          <Tab label={`Pending (${submissions.filter(s => s.status === 'pending').length})`} />
          <Tab label={`Rejected (${submissions.filter(s => s.status === 'rejected').length})`} />
        </Tabs>
      </Paper>

      <Grid container spacing={3}>
        {filteredSubmissions.map((submission) => (
          <Grid item xs={12} sm={6} md={4} key={submission.id}>
            <SubmissionCard submission={submission} />
          </Grid>
        ))}
      </Grid>

      {filteredSubmissions.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No submissions found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            You haven't submitted any clips yet. Browse campaigns to get started!
          </Typography>
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            sx={{ bgcolor: '#ff5252', '&:hover': { bgcolor: '#ff3939' } }}
          >
            Submit Your First Clip
          </Button>
        </Paper>
      )}

      {/* Submission Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        {selectedSubmission && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5">{selectedSubmission.title}</Typography>
                <Chip 
                  label={selectedSubmission.status} 
                  color={getStatusColor(selectedSubmission.status)}
                  icon={getStatusIcon(selectedSubmission.status)}
                />
              </Box>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Box
                    sx={{
                      width: '100%',
                      height: 300,
                      bgcolor: 'grey.200',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 1,
                      mb: 2
                    }}
                  >
                    <PlayIcon sx={{ fontSize: 64, color: 'grey.400' }} />
                  </Box>
                  
                  {selectedSubmission.feedback && (
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CommentIcon />
                        Feedback
                      </Typography>
                      <Typography variant="body1">
                        {selectedSubmission.feedback}
                      </Typography>
                    </Paper>
                  )}
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Submission Details</Typography>
                    <List dense>
                      <ListItem>
                        <ListItemText 
                          primary="Campaign" 
                          secondary={selectedSubmission.campaignTitle} 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Duration" 
                          secondary={selectedSubmission.duration} 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Upload Date" 
                          secondary={selectedSubmission.uploadDate.toLocaleDateString()} 
                        />
                      </ListItem>
                      {selectedSubmission.status === 'approved' && (
                        <>
                          <ListItem>
                            <ListItemText 
                              primary="Earnings" 
                              secondary={`$${selectedSubmission.earnings}`} 
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemText 
                              primary="Views" 
                              secondary={selectedSubmission.views.toLocaleString()} 
                            />
                          </ListItem>
                          {selectedSubmission.rating && (
                            <ListItem>
                              <ListItemText 
                                primary="Rating" 
                                secondary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Rating value={selectedSubmission.rating} precision={0.1} size="small" readOnly />
                                    <Typography variant="body2">({selectedSubmission.rating})</Typography>
                                  </Box>
                                } 
                              />
                            </ListItem>
                          )}
                        </>
                      )}
                    </List>
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialogOpen(false)}>Close</Button>
              {selectedSubmission.status === 'approved' && (
                <Button variant="contained" startIcon={<DownloadIcon />} color="success">
                  Download
                </Button>
              )}
              {selectedSubmission.status === 'rejected' && (
                <Button variant="contained" startIcon={<EditIcon />}>
                  Resubmit
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
